import { AppBadge, AppDialog } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { CommentsSection } from '@opportunities/-components/CommentsSection'
import { useCommentMarkers } from '@opportunities/-hooks/useCommentMarkers'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { queryKeys } from '@/lib/queryKeys'

interface CommentsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunity: {
    id: number
    customerName?: string | null
    salesforceProjectName?: string | null
    title: string
  } | null
}

/**
 * CommentsDialog - オポチュニティコメントダイアログ
 *
 * オポチュニティのコメント一覧表示・追加・編集・削除を行うダイアログコンポーネント
 */
export function CommentsDialog({ open, onOpenChange, opportunity }: CommentsDialogProps) {
  const queryClient = useQueryClient()
  const hasMarkedAsRead = useRef(false)
  const opportunityId = opportunity?.id ?? 0

  // 未読コメント数を取得
  const { data: commentMarkers } = useCommentMarkers(opportunityId, { enabled: open && opportunityId > 0 })
  const unreadCount = commentMarkers?.count ?? 0

  // コメント一覧が初回取得された時にcomment-markersのキャッシュを無効化してバッジを更新
  const handleCommentsLoaded = useCallback(() => {
    if (!hasMarkedAsRead.current && opportunityId > 0) {
      hasMarkedAsRead.current = true
      queryClient.invalidateQueries({
        queryKey: queryKeys.opportunity.commentMarkers(opportunityId),
      })
    }
  }, [opportunityId, queryClient])

  // ダイアログが閉じられた時にリセット
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        hasMarkedAsRead.current = false
      }
      onOpenChange(newOpen)
    },
    [onOpenChange],
  )

  if (!opportunity) return null

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={
        <div className='flex flex-col gap-1'>
          <div className='text-sm text-muted-foreground font-normal'>
            {opportunity.customerName} {opportunity.salesforceProjectName}
          </div>
          <div className='flex items-center gap-2'>
            <span className='wrap-break-word'>{opportunity.title}</span>
            {unreadCount > 0 && (
              <AppBadge color='destructive' className='h-5 min-w-5 px-1.5'>
                {unreadCount}
              </AppBadge>
            )}
          </div>
        </div>
      }
      className='sm:max-w-3xl max-h-[90vh] flex flex-col'
      footerOptions={[{ label: '閉じる', variant: 'outline', onClick: () => handleOpenChange(false) }]}
    >
      <CommentsSection opportunityId={opportunityId} editable={true} onCommentsLoaded={handleCommentsLoaded} />
    </AppDialog>
  )
}
